"use client";

import React, { useState, useEffect } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area,
    ComposedChart,
} from "recharts";

/**
 * Composant de graphique en ligne pour afficher l'XP quotidien
 * Conçu pour gérer plusieurs séries de données
 */
export default function XpLineChart({
    data = [],
    series = [],
    showGrid = true,
    showTooltip = true,
    locale = "fr",
}) {
    // Détection de la taille d'écran pour adapter les dots
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);
    // Couleurs modernes style Duolingo
    const modernColors = [
        {
            primary: "#58CC02", // Vert Duolingo
            gradient: "url(#greenGradient)",
            shadow: "#58CC0220",
        },
        {
            primary: "#FF9600", // Orange vibrant
            gradient: "url(#orangeGradient)",
            shadow: "#FF960020",
        },
        {
            primary: "#CE82FF", // Violet moderne
            gradient: "url(#purpleGradient)",
            shadow: "#CE82FF20",
        },
        {
            primary: "#00B4D8", // Bleu cyan
            gradient: "url(#blueGradient)",
            shadow: "#00B4D820",
        },
        {
            primary: "#FF4757", // Rouge corail
            gradient: "url(#redGradient)",
            shadow: "#FF475720",
        },
    ];

    // Validation des données
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-40 sm:h-64 text-gray-500 dark:text-grayDark-400">
                <div className="text-center">
                    <svg
                        className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                    </svg>
                    <p className="mt-2 mb-0 text-sm sm:text-base">Aucune donnée disponible</p>
                </div>
            </div>
        );
    }

    // Validation des séries
    if (!series || !Array.isArray(series) || series.length === 0) {
        return (
            <div className="flex items-center justify-center h-40 sm:h-64 text-gray-500 dark:text-grayDark-400">
                <div className="text-center">
                    <p className="mb-0 text-sm sm:text-base">Aucune série de données configurée</p>
                </div>
            </div>
        );
    }

    // Tooltip moderne style Duolingo
    const ModernTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-grayDark-900 p-2.5 sm:p-4 border-0 rounded-xl sm:rounded-2xl shadow-xl relative z-50">
                    <div className="text-center">
                        <p className="font-bold text-gray-800 dark:text-white text-xs sm:text-base mb-1.5 sm:mb-2">
                            {label}
                        </p>
                        {payload.map((entry, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between gap-2 sm:gap-3 mb-0.5 sm:mb-1 last:mb-0"
                            >
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                    <div
                                        className="w-2 h-2 sm:w-3 sm:h-3 rounded-full border border-white"
                                        style={{ backgroundColor: entry.color }}
                                    />
                                    <span
                                        className="font-medium text-gray-700 dark:text-grayDark-300 text-[10px] sm:text-sm"
                                        style={{ color: entry.color }}
                                    >
                                        {entry.name}
                                    </span>
                                </div>
                                <span
                                    className="font-bold text-[10px] sm:text-sm"
                                    style={{ color: entry.color }}
                                >
                                    {entry.value.toLocaleString(locale)} XP
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="w-full relative h-[180px] sm:h-[220px] lg:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={data}
                    margin={{
                        top: 10,
                        right: 15,
                        left: -15,
                        bottom: data.length > 14 ? 30 : 5,
                    }}
                >
                    {/* Définition des gradients */}
                    <defs>
                        <linearGradient
                            id="greenGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="#58CC02"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="#58CC02"
                                stopOpacity={0.1}
                            />
                        </linearGradient>
                        <linearGradient
                            id="orangeGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="#FF9600"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="#FF9600"
                                stopOpacity={0.1}
                            />
                        </linearGradient>
                        <linearGradient
                            id="purpleGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="#CE82FF"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="#CE82FF"
                                stopOpacity={0.1}
                            />
                        </linearGradient>
                        <linearGradient
                            id="blueGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="#00B4D8"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="#00B4D8"
                                stopOpacity={0.1}
                            />
                        </linearGradient>
                        <linearGradient
                            id="redGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor="#FF4757"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="100%"
                                stopColor="#FF4757"
                                stopOpacity={0.1}
                            />
                        </linearGradient>
                    </defs>

                    {showGrid && (
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#9CA3AF"
                            strokeOpacity={0.6}
                            vertical={false}
                        />
                    )}

                    <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                            fill: "#6B7280",
                            fontSize: 10,
                            fontWeight: "600",
                        }}
                        angle={data.length > 10 ? -45 : 0}
                        textAnchor={data.length > 10 ? "end" : "middle"}
                        height={data.length > 10 ? 50 : 35}
                        dy={data.length > 10 ? 5 : 8}
                        interval={data.length > 20 ? Math.floor(data.length / 10) : data.length > 10 ? 1 : 0}
                        tickFormatter={(value) => value.toLocaleString(locale)}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{
                            fill: "#6B7280",
                            fontSize: 10,
                            fontWeight: "600",
                        }}
                        dx={-5}
                        width={35}
                        tickFormatter={(value) => {
                            if (value >= 1000) {
                                return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
                            }
                            return value.toLocaleString(locale);
                        }}
                    />

                    {showTooltip && (
                        <Tooltip
                            content={<ModernTooltip />}
                            cursor={{
                                stroke: "#58CC02",
                                strokeWidth: 1,
                                strokeDasharray: "3 3",
                                strokeOpacity: 0.6,
                            }}
                            animationDuration={150}
                            allowEscapeViewBox={{ x: false, y: true }}
                        />
                    )}

                    {/* Rendu des aires avec gradient pour un effet moderne */}
                    {series.map((serie, index) => {
                        const colorConfig =
                            modernColors[index % modernColors.length];
                        const serieColor = serie.color || colorConfig.primary;

                        return (
                            <Area
                                key={`area-${serie.dataKey || serie.name}`}
                                type="monotone"
                                dataKey={serie.dataKey || "xp"}
                                stroke="none"
                                fill={colorConfig.gradient}
                                fillOpacity={0.6}
                                animationDuration={1500}
                                animationBegin={index * 200}
                                // Exclure de la tooltip pour éviter la duplication
                                hide={true}
                            />
                        );
                    })}

                    {/* Rendu des lignes modernes */}
                    {series.map((serie, index) => {
                        const colorConfig =
                            modernColors[index % modernColors.length];
                        const serieColor = serie.color || colorConfig.primary;
                        // Masquer les dots si trop de données pour améliorer la lisibilité
                        const showDots = data.length <= 14;

                        return (
                            <Line
                                key={serie.dataKey || serie.name}
                                type="monotone"
                                dataKey={serie.dataKey || "xp"}
                                stroke={serieColor}
                                strokeWidth={serie.strokeWidth || (isMobile ? 2 : 3)}
                                dot={showDots ? {
                                    fill: serieColor,
                                    strokeWidth: isMobile ? 2 : 4,
                                    stroke: "#fff",
                                    r: isMobile ? 3 : 6,
                                    filter: `drop-shadow(0 ${isMobile ? 1 : 2}px ${isMobile ? 2 : 4}px ${colorConfig.shadow})`,
                                } : false}
                                activeDot={{
                                    r: isMobile ? 5 : 8,
                                    stroke: serieColor,
                                    strokeWidth: isMobile ? 2 : 3,
                                    fill: "#fff",
                                    filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))",
                                    cursor: "pointer",
                                }}
                                name={serie.name}
                                animationDuration={1200}
                                animationBegin={index * 300}
                            />
                        );
                    })}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}
